// === IMPORTS ===
import React, { useState, useRef, useLayoutEffect } from 'react';
import {
  Coffee,
  Droplet,
  Monitor,
  Zap,
  Trash2,
  PlusCircle,
  Activity,
  Sparkles,
  Crosshair,
  ChevronRight,
  MapPin,
  ClipboardList,
  Target,
  Brain,
  Wrench,
  BookOpen,
  Bot,
  Coins,
  Lightbulb,
  Network,
  ExternalLink,
} from 'lucide-react';
import { AIAgent, OfficeRoom, AgentInfoCategory } from '../types';
import { AGENT_ROLES_CONFIG } from '../data/officeCatalog';
import { getAgentFullDetails, FullAgentInfo } from '../utils/agentDetailsHelper';
import '../styles/twin-icons-shared.css';
import './AgentContextMenu.css';

export type DynamicActionType =
  | 'coffee'
  | 'bathroom'
  | 'work'
  | 'energy_boost'
  | 'clear_queue'
  | 'add_task';

interface AgentContextMenuProps {
  agent: AIAgent;
  allAgents?: AIAgent[];
  x: number;
  y: number;
  rooms?: OfficeRoom[];
  onClose: () => void;
  onDynamicAction: (actionType: DynamicActionType) => void;
  onMoveToRoom?: (agentId: string, room: OfficeRoom) => void;
  onOpenModal: (initialTab?: AgentInfoCategory) => void;
  onFocusCamera: () => void;
}

interface InfoMenuItem {
  id: AgentInfoCategory;
  label: string;
  sublabel: string;
  icon: React.ReactNode;
  colorKey: string;
}

// === COMPONENTE: MENÚ CONTEXTUAL DE AGENTE (CLIC DERECHO) ===
export const AgentContextMenu: React.FC<AgentContextMenuProps> = ({
  agent,
  allAgents = [],
  x,
  y,
  rooms = [],
  onClose,
  onDynamicAction,
  onMoveToRoom,
  onOpenModal,
  onFocusCamera,
}) => {
  const [activeInfoHover, setActiveInfoHover] = useState<AgentInfoCategory | null>(null);
  const [isDynamicSubmenuOpen, setIsDynamicSubmenuOpen] = useState<boolean>(false);
  const [isRoomsSubmenuOpen, setIsRoomsSubmenuOpen] = useState<boolean>(false);

  const menuRef = useRef<HTMLDivElement | null>(null);
  const flyoutRef = useRef<HTMLDivElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: y, left: x });
  const [flyoutSide, setFlyoutSide] = useState<'right' | 'left'>('right');

  const roleConfig = AGENT_ROLES_CONFIG[agent.role];
  const fullDetails: FullAgentInfo = getAgentFullDetails(agent, allAgents);

  // === LAS 10 OPCIONES DE INFORMACIÓN DEL AGENTE ===
  const INFO_ITEMS: InfoMenuItem[] = [
    {
      id: 'task',
      label: 'Task',
      sublabel: `${fullDetails.task.progress}% • ${fullDetails.task.title.slice(0, 24)}...`,
      icon: <ClipboardList className="twin-icon-sm twin-agentmenu-color-task" />,
      colorKey: 'task',
    },
    {
      id: 'goal',
      label: 'Goal',
      sublabel: fullDetails.goal.primaryGoal.slice(0, 28) + '...',
      icon: <Target className="twin-icon-sm twin-agentmenu-color-goal" />,
      colorKey: 'goal',
    },
    {
      id: 'context',
      label: 'Context',
      sublabel: `${(fullDetails.context.activeSessionTokens / 1000).toFixed(1)}k tokens • RAG`,
      icon: <Brain className="twin-icon-sm twin-agentmenu-color-context" />,
      colorKey: 'context',
    },
    {
      id: 'tools',
      label: 'Tools',
      sublabel: `${fullDetails.tools.length} active MCPs / APIs`,
      icon: <Wrench className="twin-icon-sm twin-agentmenu-color-tools" />,
      colorKey: 'tools',
    },
    {
      id: 'knowledge',
      label: 'Knowledge',
      sublabel: `${fullDetails.knowledge.length} knowledge bases`,
      icon: <BookOpen className="twin-icon-sm twin-agentmenu-color-knowledge" />,
      colorKey: 'knowledge',
    },
    {
      id: 'model',
      label: 'Model',
      sublabel: fullDetails.model.modelName,
      icon: <Bot className="twin-icon-sm twin-agentmenu-color-model" />,
      colorKey: 'model',
    },
    {
      id: 'cost',
      label: 'Cost',
      sublabel: `$${fullDetails.cost.totalCostUsd} USD`,
      icon: <Coins className="twin-icon-sm twin-agentmenu-color-cost" />,
      colorKey: 'cost',
    },
    {
      id: 'state',
      label: 'State',
      sublabel: `⚡${fullDetails.state.energy}% • 🎯${fullDetails.state.focus}%`,
      icon: <Activity className="twin-icon-sm twin-agentmenu-color-state" />,
      colorKey: 'state',
    },
    {
      id: 'decisions',
      label: 'Decisions',
      sublabel: `${fullDetails.decisions.length} recorded CoT`,
      icon: <Lightbulb className="twin-icon-sm twin-agentmenu-color-decisions" />,
      colorKey: 'decisions',
    },
    {
      id: 'dependencies',
      label: 'Dependencies',
      sublabel: `${fullDetails.dependencies.length} linked agents`,
      icon: <Network className="twin-icon-sm twin-agentmenu-color-dependencies" />,
      colorKey: 'dependencies',
    },
  ];

  // === POSICIONAMIENTO INTELIGENTE SEGÚN VIEWPORT ===
  useLayoutEffect(() => {
    const winWidth = window.innerWidth;
    const winHeight = window.innerHeight;
    const menuEl = menuRef.current;

    const estimatedWidth = menuEl ? menuEl.offsetWidth : 280;
    const estimatedHeight = menuEl ? menuEl.offsetHeight : 540;

    let posX = x;
    let posY = y;

    if (x + estimatedWidth > winWidth - 16) {
      posX = Math.max(16, x - estimatedWidth);
      setFlyoutSide('left');
    } else {
      setFlyoutSide('right');
    }

    if (y + estimatedHeight > winHeight - 16) {
      posY = Math.max(16, winHeight - estimatedHeight - 16);
    }

    setMenuPos({ left: posX, top: posY });
  }, [x, y]);

  // === RENDER ===
  return (
    <div
      ref={menuRef}
      style={{ top: `${menuPos.top}px`, left: `${menuPos.left}px` }}
      className="twin-agentmenu"
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* === HEADER: RESUMEN DEL AGENTE === */}
      <div className="twin-agentmenu-header">
        <div className="twin-agentmenu-header-left">
          <div
            className="twin-agentmenu-avatar"
            style={{ backgroundColor: roleConfig?.avatarShirtColor || '#3b82f6' }}
          >
            {agent.spriteSet?.front_idle || agent.customImage ? (
              <img
                src={agent.spriteSet?.front_idle || agent.customImage}
                alt={agent.name}
                className="twin-agentmenu-avatar-img"
                style={{ imageRendering: 'pixelated' }}
              />
            ) : (
              roleConfig?.icon || '🤖'
            )}
          </div>
          <div className="twin-agentmenu-identity">
            <h4 className="twin-agentmenu-name">{agent.nickname?.trim() || agent.name}</h4>
            <p className="twin-agentmenu-role">{roleConfig?.title || agent.role}</p>
          </div>
        </div>

        <span className="twin-agentmenu-status-badge">
          {fullDetails.state.statusLabel.slice(0, 10)}
        </span>
      </div>

      {/* === SECCIÓN 1: LAS 10 DIMENSIONES DE INFORMACIÓN === */}
      <div className="twin-agentmenu-section">
        <div className="twin-agentmenu-section-title">
          <span>Agent Information</span>
          <span className="twin-agentmenu-section-count">10 Dimensions</span>
        </div>

        <div className="twin-agentmenu-item-list">
          {INFO_ITEMS.map((item) => {
            const isHovered = activeInfoHover === item.id;
            return (
              <div
                key={item.id}
                onMouseEnter={() => {
                  setActiveInfoHover(item.id);
                  setIsDynamicSubmenuOpen(false);
                  setIsRoomsSubmenuOpen(false);
                }}
                className="twin-agentmenu-item-wrap"
              >
                <button
                  type="button"
                  onClick={() => {
                    onOpenModal(item.id);
                    onClose();
                  }}
                  className={`twin-agentmenu-item ${isHovered ? 'twin-agentmenu-item--hovered' : ''}`}
                >
                  <div className="twin-agentmenu-item-content">
                    <span className="twin-agentmenu-item-icon">{item.icon}</span>
                    <div className="twin-agentmenu-item-text">
                      <div className="twin-agentmenu-item-label">
                        <span>{item.label}</span>
                      </div>
                      <div className="twin-agentmenu-item-sublabel">{item.sublabel}</div>
                    </div>
                  </div>
                  <ChevronRight className="twin-icon-xs twin-agentmenu-chevron" />
                </button>

                {/* === PANEL FLOTANTE DE VISTA PREVIA POR DIMENSIÓN === */}
                {isHovered && (
                  <div
                    ref={flyoutRef}
                    style={{ [flyoutSide === 'right' ? 'left' : 'right']: '102%', top: '-10px' }}
                    className="twin-agentmenu-flyout"
                    onMouseEnter={() => setActiveInfoHover(item.id)}
                    onMouseLeave={() => setActiveInfoHover(null)}
                  >
                    <div className="twin-agentmenu-flyout-header">
                      <div className="twin-agentmenu-flyout-header-left">
                        {item.icon}
                        <h5 className="twin-agentmenu-flyout-title">
                          {item.label} - {agent.name}
                        </h5>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          onOpenModal(item.id);
                          onClose();
                        }}
                        className="twin-agentmenu-flyout-open-link"
                      >
                        <span>Open Details</span>
                        <ExternalLink className="twin-icon-xs" />
                      </button>
                    </div>

                    {item.id === 'task' && (
                      <div className="twin-agentmenu-flyout-body">
                        <div className="twin-agentmenu-flyout-emphasis">{fullDetails.task.title}</div>
                        <p className="twin-agentmenu-flyout-desc">{fullDetails.task.description}</p>
                        <div className="twin-agentmenu-flyout-progress-block">
                          <div className="twin-agentmenu-flyout-progress-row">
                            <span>Progress</span>
                            <span className="twin-agentmenu-flyout-progress-value">{fullDetails.task.progress}%</span>
                          </div>
                          <div className="twin-agentmenu-progress-track">
                            <div className="twin-agentmenu-progress-fill twin-agentmenu-color-task-bg" style={{ width: `${fullDetails.task.progress}%` }} />
                          </div>
                        </div>
                        <div className="twin-agentmenu-flyout-footer-row">
                          <span>Queue: {fullDetails.task.queueCount} tasks</span>
                          <span>ETA: {fullDetails.task.etaFormatted}</span>
                        </div>
                      </div>
                    )}

                    {item.id === 'goal' && (
                      <div className="twin-agentmenu-flyout-body">
                        <div className="twin-agentmenu-flyout-goal-box">{fullDetails.goal.primaryGoal}</div>
                        <p className="twin-agentmenu-flyout-desc">{fullDetails.goal.strategicPurpose}</p>
                        <div className="twin-agentmenu-flyout-kpi-block">
                          <div className="twin-agentmenu-flyout-kpi-title">Key KPIs</div>
                          <div className="twin-agentmenu-flyout-kpi-list">
                            {fullDetails.goal.targetKpis.map((k, i) => (
                              <span key={i} className="twin-agentmenu-kpi-chip">{k}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {item.id === 'context' && (
                      <div className="twin-agentmenu-flyout-body">
                        <div className="twin-agentmenu-flyout-stat-row">
                          <span className="twin-agentmenu-flyout-muted">Window Tokens:</span>
                          <span className="twin-agentmenu-color-context">
                            {fullDetails.context.activeSessionTokens.toLocaleString()} / 1M
                          </span>
                        </div>
                        <div className="twin-agentmenu-flyout-mono-box">
                          {fullDetails.context.systemPromptSummary.slice(0, 140)}...
                        </div>
                        <div className="twin-agentmenu-flyout-grid2">
                          <span className="twin-agentmenu-flyout-muted">Memory: {fullDetails.context.memoryItemsCount} items</span>
                          <span className="twin-agentmenu-flyout-muted">RAG: {fullDetails.context.ragChunksLoaded} chunks</span>
                        </div>
                      </div>
                    )}

                    {item.id === 'tools' && (
                      <div className="twin-agentmenu-flyout-list-body">
                        <div className="twin-agentmenu-flyout-kpi-title">
                          MCP Servers & APIs ({fullDetails.tools.length})
                        </div>
                        {fullDetails.tools.map((t) => (
                          <div key={t.id} className="twin-agentmenu-flyout-row-card">
                            <div className="twin-agentmenu-flyout-mono-text">{t.name}</div>
                            <span className="twin-agentmenu-status-chip">{t.status}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {item.id === 'knowledge' && (
                      <div className="twin-agentmenu-flyout-list-body">
                        <div className="twin-agentmenu-flyout-kpi-title">
                          RAG Knowledge Bases ({fullDetails.knowledge.length})
                        </div>
                        {fullDetails.knowledge.map((k) => (
                          <div key={k.id} className="twin-agentmenu-flyout-row-card twin-agentmenu-flyout-row-card--stack">
                            <div className="twin-agentmenu-flyout-emphasis-sm">{k.name}</div>
                            <div className="twin-agentmenu-flyout-row-between">
                              <span>{k.type}</span>
                              <span className="twin-agentmenu-color-context">{k.tokensCount.toLocaleString()} tokens</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {item.id === 'model' && (
                      <div className="twin-agentmenu-flyout-body">
                        <div className="twin-agentmenu-flyout-emphasis-mono">{fullDetails.model.modelName}</div>
                        <div className="twin-agentmenu-flyout-grid2-boxed">
                          <div className="twin-agentmenu-flyout-mini-box">
                            <span className="twin-agentmenu-flyout-muted">Provider:</span>{' '}
                            <strong>{fullDetails.model.provider}</strong>
                          </div>
                          <div className="twin-agentmenu-flyout-mini-box">
                            <span className="twin-agentmenu-flyout-muted">Latency:</span>{' '}
                            <strong className="twin-stat-positive">{fullDetails.model.avgInferenceLatencyMs} ms</strong>
                          </div>
                          <div className="twin-agentmenu-flyout-mini-box">
                            <span className="twin-agentmenu-flyout-muted">Temperature:</span>{' '}
                            <strong className="twin-agentmenu-color-context">{fullDetails.model.temperature}</strong>
                          </div>
                          <div className="twin-agentmenu-flyout-mini-box">
                            <span className="twin-agentmenu-flyout-muted">Max Tokens:</span>{' '}
                            <strong className="twin-agentmenu-color-context">{fullDetails.model.maxOutputTokens}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                    {item.id === 'cost' && (
                      <div className="twin-agentmenu-flyout-body">
                        <div className="twin-agentmenu-flyout-emphasis-cost">${fullDetails.cost.totalCostUsd} USD</div>
                        <div className="twin-agentmenu-flyout-grid2">
                          <div>Prompt: {fullDetails.cost.totalInputTokens.toLocaleString()} tokens</div>
                          <div>Output: {fullDetails.cost.totalOutputTokens.toLocaleString()} tokens</div>
                          <div>Throughput: {fullDetails.cost.avgTokensPerSec} t/s</div>
                          <div>Requests: {fullDetails.cost.requestsCount}</div>
                        </div>
                      </div>
                    )}

                    {item.id === 'state' && (
                      <div className="twin-agentmenu-flyout-body">
                        <div className="twin-agentmenu-flyout-grid3">
                          <div className="twin-agentmenu-flyout-mini-box twin-text-center">
                            <div className="twin-agentmenu-color-energy">{fullDetails.state.energy}%</div>
                            <div className="twin-agentmenu-flyout-muted">Energy</div>
                          </div>
                          <div className="twin-agentmenu-flyout-mini-box twin-text-center">
                            <div className="twin-agentmenu-color-bladder">{fullDetails.state.bladder}%</div>
                            <div className="twin-agentmenu-flyout-muted">Restroom</div>
                          </div>
                          <div className="twin-agentmenu-flyout-mini-box twin-text-center">
                            <div className="twin-agentmenu-color-context">{fullDetails.state.focus}%</div>
                            <div className="twin-agentmenu-flyout-muted">Focus</div>
                          </div>
                        </div>
                        <div className="twin-agentmenu-flyout-muted">
                          Location: <span className="twin-agentmenu-flyout-emphasis-sm">{fullDetails.state.currentLocationName}</span>
                        </div>
                      </div>
                    )}

                    {item.id === 'decisions' && (
                      <div className="twin-agentmenu-flyout-list-body">
                        <div className="twin-agentmenu-flyout-kpi-title">Latest Autonomous Decision</div>
                        {fullDetails.decisions.slice(0, 1).map((d) => (
                          <div key={d.id} className="twin-agentmenu-flyout-row-card twin-agentmenu-flyout-row-card--stack">
                            <div className="twin-agentmenu-color-decisions twin-agentmenu-flyout-emphasis-sm">{d.trigger}</div>
                            <p className="twin-agentmenu-flyout-desc">{d.reasoning}</p>
                            <div className="twin-stat-positive twin-agentmenu-flyout-mono-text">Action: {d.chosenAction}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {item.id === 'dependencies' && (
                      <div className="twin-agentmenu-flyout-list-body">
                        <div className="twin-agentmenu-flyout-kpi-title">
                          Links with other agents ({fullDetails.dependencies.length})
                        </div>
                        {fullDetails.dependencies.map((dep, idx) => (
                          <div key={idx} className="twin-agentmenu-flyout-row-card">
                            <span className="twin-agentmenu-flyout-emphasis-sm">{dep.agentName}</span>
                            <span className="twin-agentmenu-flyout-mono-text">{dep.relationship}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* === SECCIÓN 2: ACCIONES RÁPIDAS === */}
      <div className="twin-agentmenu-actions-section">
        {/* === CONTROL DINÁMICO === */}
        <div
          className="twin-agentmenu-submenu-wrap"
          onMouseEnter={() => { setActiveInfoHover(null); setIsDynamicSubmenuOpen(true); setIsRoomsSubmenuOpen(false); }}
        >
          <button type="button" className="twin-agentmenu-action-row">
            <div className="twin-agentmenu-action-row-left">
              <Zap className="twin-icon-sm twin-agentmenu-color-context" />
              <span>Dynamic Control</span>
            </div>
            <ChevronRight className="twin-icon-xs twin-agentmenu-chevron" />
          </button>

          {isDynamicSubmenuOpen && (
            <div
              style={{ [flyoutSide === 'right' ? 'left' : 'right']: '102%', top: '0px' }}
              className="twin-agentmenu-submenu"
              onMouseEnter={() => setIsDynamicSubmenuOpen(true)}
              onMouseLeave={() => setIsDynamicSubmenuOpen(false)}
            >
              <div className="twin-agentmenu-submenu-title">Immediate Actions</div>
              <button type="button" onClick={() => { onDynamicAction('coffee'); onClose(); }} className="twin-agentmenu-submenu-btn twin-agentmenu-submenu-btn--amber">
                <Coffee className="twin-icon-sm" /> Send to Breakroom
              </button>
              <button type="button" onClick={() => { onDynamicAction('bathroom'); onClose(); }} className="twin-agentmenu-submenu-btn twin-agentmenu-submenu-btn--pink">
                <Droplet className="twin-icon-sm" /> Send to Restroom
              </button>
              <button type="button" onClick={() => { onDynamicAction('work'); onClose(); }} className="twin-agentmenu-submenu-btn twin-agentmenu-submenu-btn--emerald">
                <Monitor className="twin-icon-sm" /> Return to Workstation
              </button>
              <button type="button" onClick={() => { onDynamicAction('energy_boost'); onClose(); }} className="twin-agentmenu-submenu-btn twin-agentmenu-submenu-btn--yellow">
                <Zap className="twin-icon-sm" /> Restore Energy 100%
              </button>
              <button type="button" onClick={() => { onDynamicAction('add_task'); onClose(); }} className="twin-agentmenu-submenu-btn twin-agentmenu-submenu-btn--cyan">
                <PlusCircle className="twin-icon-sm" /> Assign Urgent Task
              </button>
              <button type="button" onClick={() => { onDynamicAction('clear_queue'); onClose(); }} className="twin-agentmenu-submenu-btn twin-agentmenu-submenu-btn--rose">
                <Trash2 className="twin-icon-sm" /> Clear Task Queue
              </button>
            </div>
          )}
        </div>

        {/* === MOVER A ZONA === */}
        {rooms.length > 0 && (
          <div
            className="twin-agentmenu-submenu-wrap"
            onMouseEnter={() => { setActiveInfoHover(null); setIsRoomsSubmenuOpen(true); setIsDynamicSubmenuOpen(false); }}
          >
            <button type="button" className="twin-agentmenu-action-row">
              <div className="twin-agentmenu-action-row-left">
                <MapPin className="twin-icon-sm twin-agentmenu-color-pink" />
                <span>Move to Zone...</span>
              </div>
              <ChevronRight className="twin-icon-xs twin-agentmenu-chevron" />
            </button>

            {isRoomsSubmenuOpen && (
              <div
                style={{ [flyoutSide === 'right' ? 'left' : 'right']: '102%', top: '0px' }}
                className="twin-agentmenu-submenu twin-agentmenu-submenu--scroll"
                onMouseEnter={() => setIsRoomsSubmenuOpen(true)}
                onMouseLeave={() => setIsRoomsSubmenuOpen(false)}
              >
                <div className="twin-agentmenu-submenu-title">Available Zones</div>
                {rooms.map((room) => (
                  <button
                    key={room.id}
                    type="button"
                    onClick={() => { if (onMoveToRoom) onMoveToRoom(agent.id, room); onClose(); }}
                    className="twin-agentmenu-room-btn"
                  >
                    <span className="twin-agentmenu-room-name">{room.name}</span>
                    <span className="twin-agentmenu-room-type">{room.type}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* === EJECUTAR TAREA IA === */}
        <button
          type="button"
          onClick={() => { onOpenModal('task'); onClose(); }}
          onMouseEnter={() => { setActiveInfoHover(null); setIsDynamicSubmenuOpen(false); setIsRoomsSubmenuOpen(false); }}
          className="twin-agentmenu-ai-task-btn"
        >
          <Sparkles className="twin-icon-sm" />
          <span>Execute AI Task (Gemini)...</span>
        </button>

        {/* === CENTRAR CÁMARA === */}
        <button
          type="button"
          onClick={() => { onFocusCamera(); onClose(); }}
          onMouseEnter={() => { setActiveInfoHover(null); setIsDynamicSubmenuOpen(false); setIsRoomsSubmenuOpen(false); }}
          className="twin-agentmenu-camera-btn"
        >
          <Crosshair className="twin-icon-sm twin-agentmenu-flyout-muted" />
          <span>Center Camera on Agent</span>
        </button>
      </div>
    </div>
  );
};